import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { onTourImageError } from "@/lib/imageFallback";

const posts = [
  {
    slug: "mejores-tours-la-paz",
    titleKey: "blog.title_mejoresToursLaPaz",
    excerptKey: "blog.excerpt_mejoresToursLaPaz",
    image: "https://images.unsplash.com/photo-1505228395891-9a51e7e86bf6?w=1200&q=80&auto=format&fit=crop",
    date: "2026-05-10",
    readTime: "5 min",
    categoryKey: "blog.catDestinos",
  },
  {
    slug: "guia-whale-watching",
    titleKey: "blog.title_guiaWhaleWatching",
    excerptKey: "blog.excerpt_guiaWhaleWatching",
    image: "https://images.unsplash.com/photo-1568430462989-44163eb1752f?w=1200&q=80&auto=format&fit=crop",
    date: "2026-04-28",
    readTime: "7 min",
    categoryKey: "blog.catNaturaleza",
  },
  {
    slug: "gastronomia-baja",
    titleKey: "blog.title_gastronomiaBaja",
    excerptKey: "blog.excerpt_gastronomiaBaja",
    image: "https://images.unsplash.com/photo-1565299543923-37dd37887442?w=1200&q=80&auto=format&fit=crop",
    date: "2026-04-15",
    readTime: "6 min",
    categoryKey: "blog.catGastronomia",
  },
  {
    slug: "consejos-primeriza-baja",
    titleKey: "blog.title_consejosPrimerizaBaja",
    excerptKey: "blog.excerpt_consejosPrimerizaBaja",
    image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80&auto=format&fit=crop",
    date: "2026-03-30",
    readTime: "8 min",
    categoryKey: "blog.catConsejos",
  },
  {
    slug: "pesca-deportiva-cabo",
    titleKey: "blog.title_pescaDeportivaCabo",
    excerptKey: "blog.excerpt_pescaDeportivaCabo",
    image: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=1200&q=80&auto=format&fit=crop",
    date: "2026-03-12",
    readTime: "5 min",
    categoryKey: "blog.catAventura",
  },
  {
    slug: "renta-casas-playa",
    titleKey: "blog.title_rentaCasasPlaya",
    excerptKey: "blog.excerpt_rentaCasasPlaya",
    image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&q=80&auto=format&fit=crop",
    date: "2026-02-20",
    readTime: "6 min",
    categoryKey: "blog.catHospedaje",
  },
];

export default function BlogPage() {
  const { t, i18n } = useTranslation("blog");
  const locale = i18n.language === "es" ? "es-MX" : "en-US";

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20">
      <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h1 className="font-display text-2xl md:text-4xl font-bold text-charcoal mb-3">{t("blog.title")}</h1>
            <p className="text-sm text-gray-500 max-w-lg mx-auto">{t("blog.subtitle")}</p>
          </div>

          {/* Featured */}
          <Link to={`/blog/${posts[0].slug}`} className="block mb-10 group">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="h-56 md:h-72 overflow-hidden">
                  <img
                    src={posts[0].image}
                    alt={t(posts[0].titleKey)}
                    onError={onTourImageError}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <span className="inline-block w-fit bg-ocean/10 text-ocean text-xs font-medium px-3 py-1 rounded-full mb-3">
                    {t(posts[0].categoryKey)}
                  </span>
                  <h2 className="font-display text-xl md:text-2xl font-bold text-charcoal mb-3 group-hover:text-ocean transition-colors">
                    {t(posts[0].titleKey)}
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">{t(posts[0].excerptKey)}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-400 mt-auto">
                    <span>{new Date(posts[0].date).toLocaleDateString(locale)}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span>{posts[0].readTime} {t("blog.read")}</span>
                  </div>
                </div>
              </div>
            </div>
          </Link>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.slice(1).map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="group">
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden h-full flex flex-col">
                  <div className="h-44 overflow-hidden">
                    <img
                      src={post.image}
                      alt={t(post.titleKey)}
                      onError={onTourImageError}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="inline-block w-fit bg-ocean/10 text-ocean text-[10px] font-medium px-2.5 py-0.5 rounded-full mb-2">
                      {t(post.categoryKey)}
                    </span>
                    <h3 className="font-semibold text-charcoal text-sm mb-2 group-hover:text-ocean transition-colors line-clamp-2">
                      {t(post.titleKey)}
                    </h3>
                    <p className="text-xs text-gray-500 line-clamp-2 mb-3 flex-1">{t(post.excerptKey)}</p>
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-auto">
                      <span>{new Date(post.date).toLocaleDateString(locale)}</span>
                      <span className="w-1 h-1 rounded-full bg-gray-300" />
                      <span>{post.readTime} {t("blog.read")}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}